import React, {memo, useCallback, useMemo} from 'react';
import {twMerge} from 'tailwind-merge';
import Button from 'components/ui/Button';
import DropdownMenu, {
	DropdownMenuContent,
	DropdownMenuItem,
} from 'components/ui/DropdownMenu';
import {Layers} from 'lucide-react';
import {useIntl} from 'contexts/IntlContext';
import useOverlayDockStore from 'stores/overlayDockStore';
import OverlayToolPanel from './OverlayToolPanel';
import {overlayTools, overlayToolsById} from './tools';

const OverlayDock = memo(({className}) => {
	const intl = useIntl();

	const openToolIds = useOverlayDockStore(s => s.openToolIds);
	const openTool = useOverlayDockStore(s => s.openTool);
	const closeTool = useOverlayDockStore(s => s.closeTool);

	const bottom = true;

	const handleOpenTool = useCallback(
		(toolId, closeMenu) => {
			openTool(toolId);
			closeMenu?.();
		},
		[openTool],
	);

	const fabTrigger = useMemo(
		() => (
			<Button
				variant="dark"
				size="sm"
				className={twMerge(
					'pr-3 pl-1.5 rounded-r-none rounded-l-full',
					'translate-x-4',
					'opacity-70',
					'hover:translate-x-3.5 hover:opacity-100 transition-all duration-300',
					className,
				)}
				aria-label={intl.formatMessage({id: 'Open overlay tools'})}
			>
				<Layers className="size-4" />
			</Button>
		),
		[intl],
	);

	const panels = useMemo(
		() =>
			openToolIds
				.map(toolId => overlayToolsById[toolId])
				.filter(Boolean)
				.map(tool => {
					const ToolComponent = tool.Component;
					return (
						<OverlayToolPanel
							key={tool.id}
							toolId={tool.id}
							titleMessageId={tool.titleMessageId}
							icon={tool.icon}
							onClose={() => closeTool(tool.id)}
						>
							<ToolComponent />
						</OverlayToolPanel>
					);
				}),
		[openToolIds, closeTool],
	);

	const toolMenu = useMemo(
		() => (
			<DropdownMenu trigger={fabTrigger} placement={bottom ? 'top-end' : 'bottom-end'}>
				{close => (
					<DropdownMenuContent>
						<div className="px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
							{intl.formatMessage({id: 'Overlay tools'})}
						</div>
						{overlayTools.map(tool => (
							<DropdownMenuItem
								key={tool.id}
								disabled={openToolIds.includes(tool.id)}
								onClick={() => handleOpenTool(tool.id, close)}
							>
								{intl.formatMessage({id: tool.titleMessageId})}
							</DropdownMenuItem>
						))}
					</DropdownMenuContent>
				)}
			</DropdownMenu>
		),
		[fabTrigger, bottom, intl, handleOpenTool, openToolIds],
	);

	// For bottom corners we want panels above the FAB; for top corners panels below.
	return (
		<div
			className={twMerge(
				'fixed z-50 flex flex-col gap-3 pointer-events-none',
				'bottom-20 right-2 items-end',
				className,
			)}
		>
			{bottom ? (
				<>
					<div className="flex flex-col gap-3 pointer-events-auto">{panels}</div>
					<div className="pointer-events-auto">{toolMenu}</div>
				</>
			) : (
				<>
					<div className="pointer-events-auto">{toolMenu}</div>
					<div className="flex flex-col gap-3 pointer-events-auto">{panels}</div>
				</>
			)}
		</div>
	);
});

OverlayDock.displayName = 'OverlayDock';

export default OverlayDock;
